<template>
    <card title="Autopilot" :isFullPage="true">
        <div class="row">
            <!-- Search -->
            <div class="col">
                <fg-input :iconClasses="['clickable']" @clickedIcon="search" @keyupEnter="search"
                    addonRightIcon="search" class="search" placeholder="Search" type="text" v-model="searchText" />
            </div>
            <!-- CSV Import -->
            <div class="col d-flex justify-content-end">
                <b-form-file v-if="showCsvImport" ref="importCsvRef" multiple accept=".csv" placeholder="Import CSV files" drop-placeholder="Drop CSV files"
                    :disabled="isImportingCsv" @input="importCsvFiles" class="import-csv" />
            </div>
        </div>
        <div class="row">
            <!-- Status Filter -->
            <div class="col">
                <category-filter ref="statusFilterRef" title="Status" :categories="statusFilterCategories"
                    @filter="filterByStatus" />
            </div>
            <!-- Auto Update -->
            <div v-if="showCsvImport || showUpdateButton" class="col d-flex justify-content-end">
                <div v-if="this.polling === null">Auto-update stopped <a href="#"
                        @click.prevent="startPolling">Start</a></div>
                <div v-else>Auto-update in {{ pollingTimeRemainingDisplay }} <a href="#"
                        @click.prevent="stopPolling">Stop</a></div>
            </div>
        </div>
        <!-- Pagination -->
        <pagination-display v-if="enrollments" :skip="skip" :count="count" :perPage="perPage" :lastIndex="lastIndex"
            @updatePerPage="updatePerPage" />

        <!-- Current search and filter -->
        <div
            class="alert alert-secondary text-dark m-0 p-0 mb-2">
            <span v-if="searchQuery" class="pl-2">
                Search: <strong>{{ searchQuery }}</strong>&nbsp;<button class="btn btn-sm btn-link text-info pl-0"
                    @click="clearSearch">Clear</button>
            </span>
            <span v-if="statusFilter.length > 0" class="pl-2">
                Status: <strong>{{ statusFilter.map(e => e.label).join(', ') }}</strong>&nbsp;<button
                    class="btn btn-sm btn-link text-info pl-0"
                    @click="$refs.statusFilterRef.clearSelection">Clear</button>
            </span>
        </div>

        <b-table :busy="isLoading" :fields="filteredTableFields" :items="enrollments" :no-local-sorting="true"
            :sort-by="sortBy" :sort-desc.sync="sortDesc"
            :tbody-tr-class="item => item && item._showDetails ? 'expanded-row text-nowrap' : 'text-nowrap'" @sort-changed="sortingChanged"
            id="enrollments-table" ref="table" show-empty>
            <template v-slot:table-busy>
                <div class="text-center text-danger my-2">
                    <b-spinner class="align-middle mr-3"></b-spinner>
                    <strong>Loading...</strong>
                </div>
            </template>

            <template v-slot:cell(latest_result.updated_at)="row">
                <span v-if="row.item.latest_result">{{ row.item.latest_result.updated_at + "+00:00" | prettyDateTime
                    }}</span>
            </template>

            <template v-slot:cell(system_manufacturer)="row">
                {{ row.item.system_manufacturer }} {{ row.item.system_product_name }}
            </template>

            <template v-slot:cell(latest_result.status)="row">
                <div v-if="row.item.latest_result === null">
                    <span class="text-info font-weight-bold">?</span>&nbsp;<span>Never checked</span>
                </div>
                <div v-else-if="row.item.latest_result.status === 'not enrolled'">
                    <span class="text-success">✔</span>&nbsp;<span>Not enrolled</span>
                </div>
                <div v-else-if="row.item.latest_result.status === 'enrolled'">
                    <span class="text-danger">✖</span>&nbsp;<span>Enrolled</span>
                </div>
                <div v-else-if="row.item.latest_result.status === 'unknown'">
                    <b-spinner variant="info" label="Spinning" small></b-spinner>&nbsp;<span>Updating</span>
                </div>
                <div v-else>
                    <span class="text-info font-weight-bold">?</span>&nbsp;<span>{{ row.item.latest_result.status
                        | capitalize }}</span>
                </div>
            </template>

            <template v-slot:cell(view_history)="row">
                <button @click="$set(row.item, '_showDetails', !row.item._showDetails)"
                    :disabled="row.item.enrollment_results.length < 2" class="page-link sub-table-button">
                    History <font-awesome-icon :icon="row.item._showDetails ? 'angle-down' : 'angle-up'" />
                </button>
            </template>

            <template v-slot:cell(update_status)="row">
                <button v-if="showUpdateButton" class="page-link sub-table-button" @click="autopilotReimport(row.item.id)"
                    :disabled="row.item.latest_result && row.item.latest_result.status === 'unknown'">Update</button>
            </template>

            <template v-slot:row-details="row">
                <div class="sub-table-container">
                    <b-table class="sub-table" thead-class="d-none" show-empty
                        :items="row.item.enrollment_results.slice(1)" :fields="[
                            { key: 'status', thStyle: { width: '80px' } },
                            { key: 'updated_at', thStyle: { width: '80px' } },
                            { key: 'message', thStyle: { width: '80px' } },
                        ]">
                        <template v-slot:cell(updated_at)="row">
                            <span>{{ row.item.updated_at + "+00:00" | prettyDateTime }}</span>
                        </template>
                        <template v-slot:cell(status)="row">
                            <div v-if="row.item.status === 'not enrolled'">
                                <span class="text-success">✔</span>&nbsp;<span>Not enrolled</span>
                            </div>
                            <div v-else-if="row.item.status === 'enrolled'">
                                <span class="text-danger">✖</span>&nbsp;<span>Enrolled</span>
                            </div>
                            <div v-else-if="row.item.status === 'unknown'">
                                <b-spinner variant="info" label="Updating" small></b-spinner>&nbsp;<span>Updating</span>
                            </div>
                            <div v-else>
                                <span class="text-info font-weight-bold">?</span>&nbsp;<span>{{ row.item.status |
                                    capitalize }}</span>
                            </div>
                        </template>
                    </b-table>
                </div>
            </template>
        </b-table>
        <b-pagination :per-page="perPage" :total-rows="count" @change="fetchEnrollments" v-model="currentPage" />
    </card>
</template>

<script>
import Card from '../Cards/Card';
import CategoryFilter from '../Inputs/CategoryFilter.vue';
import PaginationDisplay from './PaginationDisplay.vue';
import { mapGetters } from 'vuex';

import { notify } from '../../services/notify';

export default {
    name: 'EnrollmentsTable',
    components: {
        Card,
        CategoryFilter,
        PaginationDisplay,
    },
    data() {
        return {
            isLoading: false,
            polling: null,
            pollingInterval: 60,
            pollingTimeRemaining: 0,
            pollingTimeout: null,
            pollingTimeoutInterval: 30 * 60,
            currentPage: 1,
            searchText: '',
            showCsvImport: true,
            showUpdateButton: true,
            isImportingCsv: false,
            statusFilterCategories: [
                { value: 'never checked', label: 'Never checked' },
                { value: 'unknown', label: 'Updating' },
                { value: 'not enrolled', label: 'Not Enrolled' },
                { value: 'enrolled', label: 'Enrolled' },
                { value: 'error', label: 'Error' },
            ],
        };
    },
    methods: {
        async fetchEnrollments(page) {
            if (page) {
                this.currentPage = page;
            }

            this.isLoading = true;
            await this.$store.dispatch('enrollment/fetchEnrollments', {
                skip: this.skip,
                limit: this.perPage,
            });
            this.isLoading = false;
            this.pollingTimeRemaining = this.pollingInterval;
        },
        async poll() {
            this.pollingTimeRemaining--;
            if (this.pollingTimeRemaining === 0) {
                await this.fetchEnrollments();
            }
            this.polling = window.setTimeout(this.poll, 1000);
        },
        startPolling() {
            if (this.polling !== null) return;
            this.pollingTimeRemaining = this.pollingInterval;
            this.polling = window.setTimeout(this.poll, 1000);
            this.pollingTimeout = window.setTimeout(this.stopPolling, this.pollingTimeoutInterval * 1000);
        },
        stopPolling() {
            window.clearTimeout(this.pollingTimeout);
            this.pollingTimeout = null;
            window.clearInterval(this.polling);
            this.polling = null;
        },
        async changePage(page) {
            await this.fetchEnrollments(page);
        },
        updatePerPage(perPage) {
            this.perPage = perPage;
            this.fetchEnrollments(1);
        },
        sortingChanged(context) {
            this.sortBy = context.sortBy;
            this.sortDesc = context.sortDesc;
            this.fetchEnrollments();
        },
        search() {
            this.searchQuery = this.searchText;
            this.fetchEnrollments(1);
        },
        clearSearch() {
            this.searchText = '';
            this.search();
        },
        filterByStatus(statuses) {
            this.statusFilter = statuses;
            this.fetchEnrollments(1);
        },
        async autopilotReimport(enrollmentId) {
            try {

                await this.$store.dispatch('enrollment/autopilotReimport', enrollmentId);
                this.startPolling();
                await this.fetchEnrollments();

            } catch (error) {
                notify().error(error.message);
            }
        },
        async importCsvFiles(files) {
            if (files === null || files === undefined) return;
            // TODO This (and the notify usage) should be confined to the store
            const importCsv = async (file) => {
                const text = await file.text();
                const lines = text.split(/\r?\n/);
                if (lines.length === 0 || lines[0] !== 'system_manufacturer,system_product_name,system_serial_number,system_uuid,hardware_identifier') {
                    throw new Error('Invalid csv header');
                }
                if (lines.length < 2) {
                    throw new Error('Invalid csv body');
                }

                let enrollments = [];
                for (let i = 1; i < lines.length; ++i) {
                    const line = lines[i];
                    if (line.trim().length === 0) continue;
                    const values = [];
                    line.split(',').forEach(element => {
                        const e = element.trim();
                        if (e.length > 0) values.push(e);
                    });
                    if (values.length !== 5) {
                        throw new Error('Invalid number of fields');
                    }

                    const enrollment = {
                        system_manufacturer: values[0],
                        system_product_name: values[1],
                        system_serial_number: values[2],
                        system_uuid: values[3],
                        hardware_identifier: values[4],
                    };
                    enrollments.push(enrollment);
                }

                for (const e of enrollments) {
                    await this.$store.dispatch('enrollment/autopilotImport', e);
                }
            };

            this.isImportingCsv = true;
            let ok = false;
            for (const file of files) {
                try {
                    await importCsv(file);
                    ok = true;
                } catch (error) {
                    console.error(error);
                    notify().error(`Failed to import ${file.name}`);
                }
            }
            if (ok) {
                this.startPolling();
                this.fetchEnrollments();
                notify().success('Import successful.')
            }
            this.isImportingCsv = false;
            this.$refs.importCsvRef.reset();
        },
    },
    computed: {
        ...mapGetters('user', ['isSuperUser', 'isStaffUser', 'isPartner']),
        ...mapGetters('enrollment', ['count', 'enrollments', 'tableFields']),
        filteredTableFields() {
            if (this.isStaffUser || this.isSuperUser) {
                return this.tableFields;
            }
            else {
                return this.tableFields.filter(item => item.key !== 'organization.name');
            }
        },
        sortBy: {
            get() {
                return this.$store.state.enrollment.sortBy;
            },
            set(value) {
                this.$store.commit('enrollment/sortBy', value);
            }
        },
        sortDesc: {
            get() {
                return this.$store.state.enrollment.sortDesc;
            },
            set(value) {
                this.$store.commit('enrollment/sortDesc', value);
            }
        },
        perPage: {
            get() {
                return this.$store.state.enrollment.perPage;
            },
            set(value) {
                this.$store.commit('enrollment/perPage', value);
            }
        },
        searchQuery: {
            get() {
                return this.$store.state.enrollment.searchQuery;
            },
            set(value) {
                this.$store.commit('enrollment/searchQuery', value);
            }
        },
        statusFilter: {
            get() {
                return this.$store.state.enrollment.statusFilter;
            },
            set(value) {
                this.$store.commit('enrollment/statusFilter', value);
            }
        },
        skip() {
            return (this.currentPage - 1) * this.perPage;
        },
        lastIndex() {
            return this.skip + this.enrollments.length;
        },
        pollingTimeRemainingDisplay() {
            return String(Math.floor(this.pollingTimeRemaining / 60) % 60).padStart(2, '0')
                + 'm' + String(this.pollingTimeRemaining % 60).padStart(2, '0') + 's';
        },
    },
};

</script>

<style>
.expanded-row {
    background-color: rgba(236, 237, 237, 0.5);
}

.sub-table {
    max-width: 50%;
    overflow: scroll;
    margin-left: -15px;
}

.sub-table-button {
    position: relative;
    display: inline;
    color: #0084CB !important;
    line-height: 1.25;
    background-color: #fff;
    border: 1px solid #dee2e6;
    border-radius: 4px;
}

.search {
    max-width: 400px;
}
.import-csv {
    max-width: 400px;
}
.custom-file-label {
    background-color: #fcfdf3 !important;
    height: 40px !important;
}
.custom-file-label::after {
    height: 38px !important;
}
</style>
